import { DateTime } from 'luxon'

import type { Address } from '@consumer/graphql'

/**
 * Comparator for sorting event log entries by timestamp descending (newest first).
 * Pass directly to Array.prototype.sort.
 */
export const sortBy = (
  a: { timestamp: string },
  b: { timestamp: string }
): number => {
  if (a.timestamp < b.timestamp) return 1
  if (a.timestamp > b.timestamp) return -1
  return 0
}

/**
 * Formats an ISO timestamp as a full locale date-time string
 * (e.g. "August 5, 2026 at 8:30 AM MDT"), optionally in a given IANA time zone.
 */
export const formatDate = (input: string, timeZone?: string): string =>
  DateTime.fromISO(input).setZone(timeZone).toLocaleString(DateTime.DATETIME_FULL)

/** Fallback shown when a delivery date is not yet available. */
export const DELIVERY_DATE_TBC = 'Delivery Date to be Confirmed'

/**
 * Formats a delivery date ISO string as a full locale date
 * (e.g. "August 5, 2026"). Returns a fallback string when the date is absent.
 */
export const formatDeliveryDate = (deliveryDate?: string): string => {
  if (!deliveryDate) return DELIVERY_DATE_TBC
  return DateTime.fromISO(deliveryDate).toLocaleString(DateTime.DATE_FULL)
}

/**
 * Formats an Address as a single-line "City, State ZIP" string.
 * Returns '-' when address is null/undefined.
 *
 * Distinct from formatAddress, which renders a multi-line full address
 * including recipient name and street.
 */
export const formatCityStateZip = (address: Address | null | undefined): string => {
  if (!address) return '-'
  return `${address.city ?? ''}, ${address.state ?? ''} ${address.zipCode ?? ''}`
}
